import { PayloadAction, createSlice } from '@reduxjs/toolkit';

interface AsideSliceState {
  root: AsideInterface | null;
}

export interface AsideInterface {
  component: string;
  title: string;
  data: Record<string, any>;
  child?: AsideInterface;
  parent?: string;
  callback?: (e: any) => void;
  customWidth?: string;
  hide?: boolean;
  focused?: boolean;
}

const initialState: AsideSliceState = {
  root: null,
};

export const findAside = (root: AsideInterface, title: string) => {
  let current: AsideInterface | undefined = root;

  while (current) {
    if (current.title === title) {
      return current;
    }

    current = current.child;
  }
};

export const findLastAside = (root: AsideInterface) => {
  let current = root;

  while (current.child) {
    current = current.child;
  }

  return current;
};

const clearFocus = (aside?: AsideInterface) => {
  if (!aside) return;
  aside.focused = false;
  if (aside.child) clearFocus(aside.child);
};

const asideSlice = createSlice({
  name: 'aside',
  initialState,
  reducers: {
    setRootAside: (
      state,
      action: PayloadAction<{
        component: string;
        title: string;
        data?: Record<string, any>;
        hide?: boolean;
        focused?: boolean;
      }>
    ) => {
      state.root = {
        component: action.payload.component,
        title: action.payload.title,
        data: action.payload.data || {},
        hide: action.payload.hide,
        focused: true,
      };
    },
    updateAside: (
      state,
      action: PayloadAction<{ props: Record<string, any>; title: string }>
    ) => {
      const { props, title } = action.payload;
      if (!state.root) {
        return;
      }

      const toUpdate = findAside(state.root, title);
      if (!toUpdate) {
        return;
      }

      toUpdate.data = {
        ...toUpdate.data,
        ...props,
      };
    },
    clearAsides: (state) => {
      state.root = null;
    },
    clearAside: (state, action: PayloadAction<string>) => {
      const title = action.payload;
      if (!state.root) {
        return;
      }

      if (state.root.title === title) {
        state.root = null;
        return;
      }

      const aside = findAside(state.root, title);
      if (!aside?.parent) {
        return;
      }

      const parent = findAside(state.root, aside.parent);
      if (parent?.child) {
        parent.child = undefined;
      }
    },
    clearChild(state, action: PayloadAction<string>) {
      const title = action.payload;
      if (!state.root) {
        return;
      }

      if (state.root.title === title) {
        state.root.child = undefined;
        return;
      }

      const aside = findAside(state.root, title);
      if (!aside) {
        return;
      }

      aside.child = undefined;
    },

    switchAside: (state, action: PayloadAction<string>) => {
      if (!state.root) return;

      const title = action.payload;

      const walk = (aside?: AsideInterface) => {
        if (!aside) return;

        aside.focused = aside.title === title;

        if (aside.child) {
          walk(aside.child);
        }
      };

      walk(state.root);
    },

    stackAside: (state, action: PayloadAction<AsideInterface>) => {
      if (!state.root) return;

      clearFocus(state.root); // ✅ alles defocusen

      const existing = findAside(state.root, action.payload.title);

      if (existing) {
        existing.component = action.payload.component;
        existing.data = action.payload.data || {};
        existing.callback = action.payload.callback;
        existing.child = undefined;
        existing.focused = true; // ✅ focus
        return;
      }

      const lastAside = findLastAside(state.root);

      lastAside.child = {
        component: action.payload.component,
        title: action.payload.title,
        data: action.payload.data || {},
        callback: action.payload.callback,
        parent: lastAside.title,
        focused: true,
      };
    },

    hideAside: (state, action: PayloadAction<boolean>) => {
      if (!state.root) return;
      state.root.hide = action.payload;
    },
  },
});

export const {
  setRootAside,
  stackAside,
  updateAside,
  clearAsides,
  clearAside,
  clearChild,
  hideAside,
  switchAside,
} = asideSlice.actions;
export default asideSlice.reducer;
